/* global BigInt */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './GaugeSearch.css';
import { ethers } from 'ethers';
import apiClient from '../api/client';

import FavoritesTable from './FavoritesTable';

// Gauge Controller configuration
const GAUGE_CONTROLLER_ADDRESS = '0x2f50d538606fa9edd2b11e2446beb18c9d5846bb';
const GAUGE_CONTROLLER_ABI = [
  'function gauge_types(address gauge) view returns (int128)',
];

// Create ethers provider for mainnet - using multiple fallback endpoints
const createProvider = () => {
  const rpcUrls = [
    'https://ethereum.publicnode.com',
    'https://rpc.ankr.com/eth',
    'https://eth.llamarpc.com',
    'https://cloudflare-eth.com',
  ];

  // Try each RPC URL until one works
  for (const url of rpcUrls) {
    try {
      return new ethers.JsonRpcProvider(url);
    } catch (error) {
      // Silent failure, try next URL
    }
  }

  // Fallback to default
  return new ethers.JsonRpcProvider('https://ethereum.publicnode.com');
};

const provider = createProvider();

// Helper function to retry API calls
const retryApiCall = async (apiCall, maxRetries = 3, initialDelay = 1000) => {
  let lastError = null;
  let delay = initialDelay;
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;

      // Don't wait on the last attempt
      if (attempt < maxRetries) {
        const currentDelay = delay;
        await new Promise((resolve) => setTimeout(resolve, currentDelay));
        // Increase delay for next retry (exponential backoff)
        delay *= 1.5;
      }
    }
  }

  // All retries failed
  throw lastError;
};

// Helper function to abbreviate addresses for display (with 0x prefix)
function abbreviateAddress(address) {
  if (!address || typeof address !== 'string' || address.length < 10) {
    return address;
  }

  // Keep 0x prefix and abbreviate the rest
  if (address.startsWith('0x')) {
    const cleanAddress = address.slice(2);
    if (cleanAddress.length < 8) {
      return address; // Return full address if too short
    }
    return `0x${cleanAddress.slice(0, 4)}…${cleanAddress.slice(-4)}`;
  } else {
    // Handle case where address doesn't start with 0x
    if (address.length < 8) {
      return address;
    }
    return `${address.slice(0, 4)}…${address.slice(-4)}`;
  }
}

// Function to fetch search suggestions
const fetchSearchSuggestions = async (query) => {
  if (!query || query.length < 3) {
    return [];
  }

  // Check if it's already a gauge address format - if so, skip search API
  if (query.match(/^0x[0-9a-fA-F]{40}$/i)) {
    return [];
  }

  try {
    const response = await retryApiCall(() =>
      apiClient.get('/api/gauge/search', { params: { q: query } })
    );

    // Return the results array from the API response
    return response.data?.data?.results || [];
  } catch (error) {
    console.warn('Search suggestions failed:', error.message);
    return [];
  }
};

// Protocol mapping for icons (same as APRChart)
const protocolIcons = {
  asdCRV: {
    name: 'asdCRV',
    iconUrl:
      'https://assets.coingecko.com/coins/images/13724/standard/stakedao_logo.jpg?1696513468',
  },
  yvyCRV: {
    name: 'yvyCRV',
    iconUrl:
      'https://assets.coingecko.com/coins/images/11849/standard/yearn.jpg?1696511720',
  },
  ucvxCRV: {
    name: 'ucvxCRV',
    iconUrl:
      'https://assets.coingecko.com/coins/images/15585/standard/convex.png?1696515221',
  },
};

// Helper function to get protocol icon from provider name
const getProtocolIcon = (providerName) => {
  // Find matching protocol based on provider name
  for (const [key, protocol] of Object.entries(protocolIcons)) {
    if (providerName.toLowerCase().includes(key.toLowerCase())) {
      return protocol;
    }
  }
  return null;
};

const GaugeSearch = ({
  favorites,
  toggleFavorite,
  isFavorite,
  removeFavorite,
}) => {
  const [address, setAddress] = useState('');
  const [gaugeDetails, setGaugeDetails] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [boostData, setBoostData] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState({
    basic: false,
    verification: false,
    boosts: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const initialLoadDone = useRef(false);
  const [copiedText, setCopiedText] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [selectedGauge, setSelectedGauge] = useState(null); // Store selected gauge info
  const [showModal, setShowModal] = useState(false);

  // Gauge status state
  const [gaugeStatus, setGaugeStatus] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const rpcCallInProgress = useRef(false);

  // Voting data state
  const [showVotes, setShowVotes] = useState(false);
  const [voteData, setVoteData] = useState([]);
  const [voteLoading, setVoteLoading] = useState(false);
  const [voteError, setVoteError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Validate Ethereum address format - wrapped in useCallback to avoid recreating on each render
  const isValidAddress = useCallback((value) => {
    if (!value || typeof value !== 'string') return false;

    // Clean the value first
    const cleanValue = value.trim().toLowerCase();

    // Check if it matches the Ethereum address pattern (case insensitive)
    return /^0x[a-f0-9]{40}$/.test(cleanValue);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    setShowError(false);
    // Hide votes when changing address
    setShowVotes(false);

    // If user starts typing after selecting a gauge, clear the selection
    if (selectedGauge && value !== selectedGauge.name) {
      setSelectedGauge(null);
    }

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Hide suggestions if empty, less than 3 characters, or already a full address
    if (!value || value.length < 3 || value.match(/^0x[0-9a-fA-F]{40}$/i)) {
      setShowSuggestions(false);
      setSearchSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return;
    }

    // Debounce search API calls
    const newTimeout = setTimeout(async () => {
      const suggestions = await fetchSearchSuggestions(value);
      setSearchSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
      setSelectedSuggestionIndex(-1); // Reset selection when new results come in
    }, 300);

    setSearchTimeout(newTimeout);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || searchSuggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < searchSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev > 0 ? prev - 1 : searchSuggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (
          selectedSuggestionIndex >= 0 &&
          selectedSuggestionIndex < searchSuggestions.length
        ) {
          const selectedResult = searchSuggestions[selectedSuggestionIndex];
          handleSuggestionClick(selectedResult);
        } else if (showSuggestions) {
          // If suggestions are showing but none selected, don't submit form
          return;
        }
        // If no suggestions showing, let the form submit naturally
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        // No action needed for other keys
        break;
    }
  };

  const handleSuggestionClick = (gaugeResult) => {
    // Clean and validate the address from API response
    let cleanAddress = gaugeResult.gauge_address;
    if (typeof cleanAddress === 'string') {
      cleanAddress = cleanAddress.trim();
      // Ensure it starts with 0x and is lowercase for consistency
      if (!cleanAddress.startsWith('0x')) {
        cleanAddress = '0x' + cleanAddress;
      }
      cleanAddress = cleanAddress.toLowerCase();
    }

    // Store the selected gauge info and show the gauge name in the input
    setSelectedGauge({ name: gaugeResult.name, address: cleanAddress });
    setAddress(gaugeResult.name); // Show the gauge name in the input field
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setSelectedSuggestionIndex(-1);

    // Clear any pending search timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }

    // Update URL with the gauge address
    const url = new URL(window.location);
    url.searchParams.set('gauge', cleanAddress);
    window.history.pushState({}, '', url);

    // Show modal immediately, then load the gauge data
    setShowModal(true);
    fetchGaugeDetails(cleanAddress);
  };

  // Calculate gauge inflation rate (CRV tokens per second)
  const calculateGaugeInflationRate = (weight, inflationRate) => {
    if (!weight || !inflationRate) return '0 CRV/sec';

    // Convert strings to BigInt
    const weightBig = BigInt(weight);
    const inflationBig = BigInt(inflationRate);

    // Total is 10^18
    const total = BigInt(10) ** BigInt(18);

    // Calculate gauge inflation rate
    // First convert the weight to a fraction (0-1)
    const weightFraction = Number(weightBig) / Number(total);

    // Then multiply by the inflation rate (which is in wei)
    const inflationValue = Number(inflationBig);

    // The result is the gauge-specific inflation rate in wei per second
    const gaugeInflationRateWei = weightFraction * inflationValue;

    // Convert from wei to CRV (divide by 10^18)
    const gaugeInflationRateCRV = gaugeInflationRateWei / Number(total);

    // Format with 4 decimal places instead of 8
    return gaugeInflationRateCRV.toFixed(4) + ' CRV/sec';
  };

  // Format percentage from wei
  const formatPercentage = (weiValue) => {
    if (!weiValue) return '0%';

    // Convert string to BigInt
    const wei = BigInt(weiValue);
    // Total is 10^18
    const total = BigInt(10) ** BigInt(18);

    // Calculate percentage (multiply by 100 for percentage)
    const percentage = (Number(wei) * 100) / Number(total);

    return percentage.toFixed(2) + '%';
  };

  // Determine initial status based on API data
  const getInitialStatus = (gaugeData) => {
    if (gaugeData.is_killed) return 'Killed';
    if (gaugeData.has_no_crv) return 'Inactive';
    return 'Active';
  };

  // Check gauge controller status via RPC
  const checkGaugeControllerStatus = useCallback(
    async (gaugeAddress) => {
      // Prevent concurrent RPC calls
      if (rpcCallInProgress.current) {
        return;
      }

      try {
        rpcCallInProgress.current = true;
        setStatusLoading(true);

        const contract = new ethers.Contract(
          GAUGE_CONTROLLER_ADDRESS,
          GAUGE_CONTROLLER_ABI,
          provider
        );

        await contract.gauge_types(gaugeAddress);

        // If the call succeeds and returns >= 0, gauge is active in controller
        // Only update status if current gauge is not killed
        if (gaugeDetails?.data && !gaugeDetails.data.is_killed) {
          setGaugeStatus('Active');
        }
      } catch (error) {
        // EXPLICIT CONTRACT REVERT: gauge_types() function exists but reverts for this address
        // This means the gauge is NOT in the controller (should mark as Inactive)
        const isContractRevert =
          error.code === 'CALL_EXCEPTION' &&
          error.reason &&
          error.reason !== 'missing revert data' &&
          !error.message.includes('failed to detect network') &&
          !error.message.includes('missing revert data');

        if (
          isContractRevert &&
          gaugeDetails?.data &&
          !gaugeDetails.data.is_killed
        ) {
          setGaugeStatus('Inactive');
        }
        // For network failures or unclear errors, keep the original status
      } finally {
        rpcCallInProgress.current = false;
        setStatusLoading(false);
      }
    },
    [gaugeDetails]
  );

  // Function to fetch gauge details - wrapped in useCallback to prevent infinite re-renders
  const fetchGaugeDetails = useCallback(
    async (gaugeAddress) => {
      if (!isValidAddress(gaugeAddress)) {
        setError(
          'Please enter a valid Ethereum address (0x... format with 42 characters)'
        );
        setShowError(true);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        setShowError(false);
        setGaugeDetails(null);
        setVerificationData(null);
        setBoostData(null);
        setGaugeStatus('');
        setStatusLoading(false);
        rpcCallInProgress.current = false;

        // Step 1: Fetch basic gauge data (fastest ~100-300ms)
        setLoadingSteps({ basic: true, verification: false, boosts: false });

        const basicResponse = await retryApiCall(() =>
          apiClient.get('/api/gauge/basic', {
            params: { gauge: gaugeAddress },
          })
        );

        // Check if response contains valid gauge data
        if (
          basicResponse.data &&
          basicResponse.data.data &&
          Object.keys(basicResponse.data.data).length > 0
        ) {
          setGaugeDetails(basicResponse.data);
          setLoadingSteps((prev) => ({ ...prev, basic: false }));

          // Set initial status based on API data
          const initialStatus = getInitialStatus(basicResponse.data.data);
          setGaugeStatus(initialStatus);

          // If gauge is not killed, check controller status via RPC
          if (!basicResponse.data.data.is_killed) {
            checkGaugeControllerStatus(gaugeAddress);
          }
        } else {
          throw new Error('Not a valid gauge address');
        }

        // Step 2: Fetch verification data (medium ~1-2s) - run in parallel
        const verificationPromise = (async () => {
          try {
            setLoadingSteps((prev) => ({ ...prev, verification: true }));
            const verificationResponse = await retryApiCall(() =>
              apiClient.get('/api/gauge/verification', {
                params: { gauge: gaugeAddress },
              })
            );
            setVerificationData(verificationResponse.data);
          } catch (err) {
            console.warn('Verification data fetch failed:', err.message);
          } finally {
            setLoadingSteps((prev) => ({ ...prev, verification: false }));
          }
        })();

        // Step 3: Fetch boost data (medium ~0.5-1s) - run in parallel
        const boostPromise = (async () => {
          try {
            setLoadingSteps((prev) => ({ ...prev, boosts: true }));
            const boostResponse = await retryApiCall(() =>
              apiClient.get('/api/gauge/boosts', {
                params: { gauge: gaugeAddress },
              })
            );
            setBoostData(boostResponse.data);
          } catch (err) {
            console.warn('Boost data fetch failed:', err.message);
          } finally {
            setLoadingSteps((prev) => ({ ...prev, boosts: false }));
          }
        })();

        // Wait for both verification and boost data to complete
        await Promise.allSettled([verificationPromise, boostPromise]);
      } catch (err) {
        const errorMessage =
          err.message === 'Not a valid gauge address'
            ? 'Not a valid gauge address'
            : `Failed to fetch gauge details: ${err.message}`;

        setGaugeDetails(null);
        setVerificationData(null);
        setBoostData(null);
        setGaugeStatus('');
        setStatusLoading(false);
        rpcCallInProgress.current = false;
        setLoadingSteps({ basic: false, verification: false, boosts: false });
        setError(errorMessage);
        setShowError(true);
      } finally {
        setLoading(false);
      }
    },
    [isValidAddress, checkGaugeControllerStatus]
  );

  // Function to fetch vote data for a gauge with retry mechanism
  const fetchVoteData = useCallback(
    async (page = 1) => {
      if (
        !gaugeDetails ||
        !gaugeDetails.data ||
        !gaugeDetails.data.gauge_address
      ) {
        setVoteError('No gauge details available');
        return;
      }

      setVoteLoading(true);
      setVoteError(null);

      try {
        // Use retry mechanism for vote data fetch
        const response = await retryApiCall(() =>
          apiClient.get('/api/crvlol/gauge_votes', {
            params: { gauge: gaugeDetails.data.gauge_address, page },
          })
        );

        if (response.data.data.length === 0) {
          console.log('No votes found for this gauge');
          setVoteError('No votes found for this gauge');
          setVoteData([]);
        } else {
          console.log(
            `Successfully retrieved ${response.data.data.length} vote records`
          );
          console.log(
            `Total records: ${response.data.total}, Per page: ${response.data.per_page}`
          );
          setVoteData(response.data.data);
          setTotalPages(
            Math.ceil(response.data.total / response.data.per_page)
          );
          setCurrentPage(page);
        }
      } catch (err) {
        console.error('Vote fetch error details:', {
          message: err.message,
          stack: err.stack,
          response: err.response
            ? {
                status: err.response.status,
                data: err.response.data,
              }
            : 'No response',
          config: err.config
            ? {
                url: err.config.url,
                method: err.config.method,
              }
            : 'No config',
        });

        setVoteError('Error fetching vote data. Please try again.');
      } finally {
        setVoteLoading(false);
      }
    },
    [gaugeDetails]
  );

  // Toggle vote data visibility and fetch data if needed
  const toggleVoteData = () => {
    const newShowVotes = !showVotes;
    setShowVotes(newShowVotes);

    // Fetch vote data if showing votes and we haven't fetched them yet
    if (newShowVotes && voteData.length === 0 && !voteLoading) {
      fetchVoteData(1);
    }
  };

  // Formatting helpers for vote data
  const formatAddress = (address) => {
    if (address && address.length > 10) {
      return `${address.slice(0, 6)}...${address.slice(-4)}`;
    }
    return address || '';
  };

  const formatNumber = (num) => {
    return Math.round(num).toLocaleString('en-US');
  };

  const formatWeight = (weight) => {
    return ((weight / 10000) * 100).toFixed(2) + '%';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getEtherscanLink = (address) =>
    `https://etherscan.io/address/${address}`;

  // Simplify the copyToClipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        // Success - just store the text that was copied for UI feedback
        setCopiedText(text);
        setTimeout(() => {
          setCopiedText('');
        }, 1500);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Determine the address to use - either from selected gauge or direct input
    const addressToUse = selectedGauge ? selectedGauge.address : address;

    // Update URL with the gauge address
    if (addressToUse) {
      const url = new URL(window.location);
      url.searchParams.set('gauge', addressToUse);
      window.history.pushState({}, '', url);
    }

    // Hide votes when changing address
    setShowVotes(false);
    setVoteData([]);

    setShowModal(true);
    await fetchGaugeDetails(addressToUse);
  };

  // Handle gauge click from favorites table
  const handleGaugeClick = (gaugeAddress) => {
    setAddress(gaugeAddress);
    setSelectedGauge(null); // Clear any selected gauge since this is a direct address

    // Update URL
    const url = new URL(window.location);
    url.searchParams.set('gauge', gaugeAddress);
    window.history.pushState({}, '', url);

    // Show modal immediately, then fetch gauge details
    setShowModal(true);
    fetchGaugeDetails(gaugeAddress);
  };

  // Check for gauge address in URL on component mount - only run once
  useEffect(() => {
    // Skip if we've already processed the URL parameters
    if (initialLoadDone.current) return;

    const queryParams = new URLSearchParams(window.location.search);
    const gaugeAddress = queryParams.get('gauge');

    if (gaugeAddress) {
      setAddress(gaugeAddress);
      setShowModal(true);
      fetchGaugeDetails(gaugeAddress);
    }

    // Mark that we've processed the URL parameters
    initialLoadDone.current = true;
  }, [fetchGaugeDetails]);

  return (
    <div className="gauge-search-container">
      <form onSubmit={handleSubmit} className="search-form">
        <div className="search-input-container">
          <input
            type="text"
            value={address}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Enter gauge address (0x...) or gauge name"
            className="search-input"
            title={
              selectedGauge
                ? `Selected: ${selectedGauge.name} (${abbreviateAddress(
                    selectedGauge.address
                  )})`
                : ''
            }
          />
          {address && (
            <button
              type="button"
              className="search-clear-button"
              onClick={() => {
                setAddress('');
                setSelectedGauge(null);
                setShowSuggestions(false);
                setSearchSuggestions([]);
                setSelectedSuggestionIndex(-1);
                if (searchTimeout) {
                  clearTimeout(searchTimeout);
                  setSearchTimeout(null);
                }
              }}
              title="Clear search"
            >
              ×
            </button>
          )}
          {showSuggestions && (
            <div className="search-suggestions">
              {searchSuggestions.map((result, index) => (
                <div
                  key={result.gauge_address}
                  className={`search-suggestion-item ${
                    index === selectedSuggestionIndex ? 'selected' : ''
                  }`}
                  onClick={() => handleSuggestionClick(result)}
                >
                  <span className="gauge-name">{result.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="search-button"
          disabled={loading || !address}
        >
          Search for gauge
        </button>
      </form>

      {/* Favorites Table */}
      <FavoritesTable
        favorites={favorites}
        onGaugeClick={handleGaugeClick}
        onRemoveFavorite={removeFavorite}
      />

      {showError && error && <div className="error">{error}</div>}

      {loading && !gaugeDetails && (
        <div className="loading-animated">
          <div className="loading-spinner"></div>
        </div>
      )}

      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
            }
          }}
        >
          <div className="modal-content">
            <button
              className="modal-close-button"
              onClick={() => setShowModal(false)}
              title="Close"
            >
              ×
            </button>
            <div className="gauge-details">
              {loading && !gaugeDetails ? (
                <div className="modal-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading gauge details...</p>
                </div>
              ) : gaugeDetails && gaugeDetails.data ? (
                <>
                  <div className="gauge-header-container">
                    <h2 className="gauge-title">
                      <button
                        className={`favorite-button ${
                          isFavorite(gaugeDetails.data.gauge_address)
                            ? 'favorited'
                            : ''
                        }`}
                        onClick={() => toggleFavorite(gaugeDetails)}
                        title={
                          isFavorite(gaugeDetails.data.gauge_address)
                            ? 'Remove from favorites'
                            : 'Add to favorites'
                        }
                      >
                        <i
                          className={`${
                            isFavorite(gaugeDetails.data.gauge_address)
                              ? 'fas'
                              : 'far'
                          } fa-star`}
                        ></i>
                      </button>
                      {(() => {
                        const title =
                          gaugeDetails.data.curve_key ||
                          gaugeDetails.data.pool_name ||
                          'Gauge Details';
                        const parenIndex = title.indexOf('(');

                        if (parenIndex === -1) {
                          return title;
                        }

                        return title.substring(0, parenIndex).trim();
                      })()}
                    </h2>
                  </div>

                  <div className="details-grid">
                    <div className="left-column">
                      <div className="detail-card basic-info">
                        <h3>Basic Information</h3>
                        <div className="detail-item">
                          <span className="label">Gauge:</span>
                          <span className="value">
                            {abbreviateAddress(gaugeDetails.data.gauge_address)}
                            <button
                              className={`copy-button ${
                                copiedText === gaugeDetails.data.gauge_address
                                  ? 'copied'
                                  : ''
                              }`}
                              onClick={() =>
                                copyToClipboard(gaugeDetails.data.gauge_address)
                              }
                              title="Copy full address to clipboard"
                            >
                              <i
                                className={`fas ${
                                  copiedText === gaugeDetails.data.gauge_address
                                    ? 'fa-check'
                                    : 'fa-copy'
                                }`}
                              ></i>
                            </button>
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">
                            {gaugeDetails.data.lendingVaultAddress
                              ? 'Vault:'
                              : 'Pool:'}
                          </span>
                          <span className="value">
                            {abbreviateAddress(
                              gaugeDetails.data.lendingVaultAddress ||
                                gaugeDetails.data.pool_address
                            )}
                            <button
                              className={`copy-button ${
                                copiedText ===
                                (gaugeDetails.data.lendingVaultAddress ||
                                  gaugeDetails.data.pool_address)
                                  ? 'copied'
                                  : ''
                              }`}
                              onClick={() =>
                                copyToClipboard(
                                  gaugeDetails.data.lendingVaultAddress ||
                                    gaugeDetails.data.pool_address
                                )
                              }
                              title="Copy full address to clipboard"
                            >
                              <i
                                className={`fas ${
                                  copiedText ===
                                  (gaugeDetails.data.lendingVaultAddress ||
                                    gaugeDetails.data.pool_address)
                                    ? 'fa-check'
                                    : 'fa-copy'
                                }`}
                              ></i>
                            </button>
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Network:</span>
                          <span className="value">
                            {gaugeDetails.data.blockchain}
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Status:</span>
                          <span className="value">
                            {statusLoading ? (
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '5px',
                                }}
                              >
                                {gaugeStatus}
                                <div
                                  className="loading-spinner"
                                  style={{ transform: 'scale(0.4)' }}
                                ></div>
                              </span>
                            ) : (
                              gaugeStatus
                            )}
                            <span className="tooltip-container">
                              <span className="info-tooltip">i</span>
                              <span className="tooltip-text tooltip-text-left">
                                Active: Added to controller
                                <br />
                                Inactive: Not added to controller
                                <br />
                                Killed: Added, but ineligible for CRV
                              </span>
                            </span>
                          </span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Verified:</span>
                          <span
                            className={`value ${
                              verificationData?.data?.verification?.is_valid
                                ? 'valid-text'
                                : verificationData !== null
                                ? 'invalid-text'
                                : ''
                            }`}
                          >
                            {verificationData === null ? (
                              loadingSteps.verification ? (
                                <div
                                  className="loading-spinner"
                                  style={{ transform: 'scale(0.5)' }}
                                ></div>
                              ) : (
                                'Unknown'
                              )
                            ) : verificationData.data?.verification
                                ?.is_valid ? (
                              <span
                                style={{ fontWeight: 'bold', color: 'green' }}
                                title={
                                  verificationData.data?.verification
                                    ?.message || ''
                                }
                              >
                                VERIFIED
                              </span>
                            ) : (
                              <span
                                style={{ fontWeight: 'bold', color: 'red' }}
                                title={
                                  verificationData.data?.verification
                                    ?.message || ''
                                }
                              >
                                NOT VERIFIED
                              </span>
                            )}
                            <span className="tooltip-container">
                              <span className="info-tooltip">i</span>
                              <span className="tooltip-text tooltip-text-right">
                                Verified means that this gauge was deployed from
                                a trusted factory.
                              </span>
                            </span>
                          </span>
                        </div>
                        {(gaugeDetails.data.lendingVaultUrls?.deposit ||
                          gaugeDetails.data.pool_urls?.deposit) && (
                          <div className="detail-item">
                            <span className="label">Curve:</span>
                            <span className="value">
                              <a
                                href={
                                  gaugeDetails.data.lendingVaultUrls?.deposit ||
                                  gaugeDetails.data.pool_urls.deposit
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="curve-link"
                              >
                                <i className="fas fa-external-link-alt"></i>
                                View on Curve
                              </a>
                            </span>
                          </div>
                        )}
                      </div>

                      {(boostData?.data?.provider_boosts &&
                        Object.keys(boostData.data.provider_boosts).length >
                          0) ||
                      loadingSteps.boosts ||
                      gaugeStatus === 'Inactive' ? (
                        <div className="detail-card boosts">
                          <h3>Boost Providers</h3>
                          <div className="boost-providers">
                            {loadingSteps.boosts ? (
                              <div
                                style={{
                                  textAlign: 'center',
                                  padding: '20px',
                                  color: '#666',
                                }}
                              >
                                <div className="loading-spinner"></div>
                              </div>
                            ) : gaugeStatus === 'Inactive' ? (
                              <div
                                style={{
                                  textAlign: 'center',
                                  padding: '20px',
                                  color: '#666',
                                  fontSize: '12px',
                                }}
                              >
                                Not added to controller
                              </div>
                            ) : boostData?.data?.provider_boosts ? (
                              Object.entries(boostData.data.provider_boosts)
                                .sort(
                                  ([, a], [, b]) =>
                                    parseFloat(b.boost_formatted) -
                                    parseFloat(a.boost_formatted)
                                )
                                .map(([provider, boostData]) => {
                                  const boostValue = parseFloat(
                                    boostData.boost_formatted
                                  );
                                  const isValidBoost =
                                    !isNaN(boostValue) &&
                                    boostValue > 0 &&
                                    boostData.boost_formatted !== 'N/A';

                                  // If boost is invalid/N/A or no emissions, show empty meter
                                  const fillPercentage = isValidBoost
                                    ? Math.max(
                                        0,
                                        Math.min(
                                          100,
                                          ((boostValue - 1.0) / (2.5 - 1.0)) *
                                            100
                                        )
                                      )
                                    : 0;

                                  // Color coding based on boost value ranges (only if valid)
                                  let meterColor = '#e0e0e0'; // Default light gray for empty/invalid
                                  if (isValidBoost) {
                                    if (boostValue >= 2.0) {
                                      meterColor = '#4caf50'; // Green for 2.0x+
                                    } else if (boostValue >= 1.5) {
                                      meterColor = '#ffc107'; // Yellow for 1.5x-1.99x
                                    } else {
                                      meterColor = '#ff5722'; // Red for 1.0x-1.49x
                                    }
                                  }

                                  return (
                                    <div className="boost-item" key={provider}>
                                      <div className="provider-name">
                                        <div
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                          }}
                                        >
                                          {(() => {
                                            const protocolIcon =
                                              getProtocolIcon(provider);
                                            return protocolIcon ? (
                                              <img
                                                src={protocolIcon.iconUrl}
                                                alt={protocolIcon.name}
                                                style={{
                                                  width: '16px',
                                                  height: '16px',
                                                  borderRadius: '50%',
                                                  border: '1px solid #e0e0e0',
                                                  objectFit: 'cover',
                                                  flexShrink: 0,
                                                }}
                                                onError={(e) => {
                                                  e.target.style.display =
                                                    'none';
                                                }}
                                              />
                                            ) : null;
                                          })()}
                                          <span>
                                            {provider}
                                            {boostData.pct_of_total_supply !=
                                              null &&
                                              boostData.pct_of_total_supply >
                                                0 && (
                                                <span className="tooltip-container">
                                                  <span className="info-tooltip">
                                                    i
                                                  </span>
                                                  <span className="tooltip-text">
                                                    {boostData.pct_of_total_supply.toFixed(
                                                      2
                                                    )}
                                                    % of staked
                                                  </span>
                                                </span>
                                              )}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="boost-value-container">
                                        <div className="boost-meter">
                                          <div
                                            className="boost-meter-fill"
                                            style={{
                                              width: `${fillPercentage}%`,
                                              backgroundColor: meterColor,
                                            }}
                                          ></div>
                                        </div>
                                        <span className="boost-value-text">
                                          {isValidBoost
                                            ? `${parseFloat(
                                                boostData.boost_formatted
                                              ).toFixed(3)}x`
                                            : 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })
                            ) : (
                              <div
                                style={{
                                  textAlign: 'center',
                                  padding: '20px',
                                  color: '#666',
                                }}
                              >
                                No boost data available
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="detail-card weights">
                      <h3>Emissions</h3>

                      {gaugeStatus === 'Inactive' ? (
                        <div
                          style={{
                            textAlign: 'center',
                            padding: '20px',
                            color: '#666',
                            fontSize: '12px',
                          }}
                        >
                          Not added to controller
                        </div>
                      ) : (
                        <>
                          <div className="weight-section">
                            <h4 className="section-header">Current</h4>
                            <div className="detail-item">
                              <span className="label">Rate:</span>
                              <span className="value">
                                {calculateGaugeInflationRate(
                                  gaugeDetails.data.gauge_controller
                                    ?.gauge_relative_weight,
                                  gaugeDetails.data.gauge_controller
                                    ?.inflation_rate
                                )}
                              </span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Weight:</span>
                              <span className="value">
                                {formatPercentage(
                                  gaugeDetails.data.gauge_controller
                                    ?.gauge_relative_weight
                                )}
                              </span>
                            </div>
                            {gaugeDetails.data.apy_data?.gauge_crv_apy && (
                              <div className="detail-item">
                                <span className="label">APR:</span>
                                <span className="value">
                                  {gaugeDetails.data.apy_data.gauge_crv_apy.min_boost?.toFixed(
                                    2
                                  ) || '0.00'}
                                  % →{' '}
                                  {gaugeDetails.data.apy_data.gauge_crv_apy.max_boost?.toFixed(
                                    2
                                  ) || '0.00'}
                                  %
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="weight-section">
                            <h4 className="section-header">Future</h4>
                            <div className="detail-item">
                              <span className="label">Rate:</span>
                              <span className="value">
                                {calculateGaugeInflationRate(
                                  gaugeDetails.data.gauge_controller
                                    ?.gauge_future_relative_weight,
                                  gaugeDetails.data.gauge_controller
                                    ?.inflation_rate
                                )}
                              </span>
                            </div>
                            <div className="detail-item">
                              <span className="label">Weight:</span>
                              <span className="value">
                                {formatPercentage(
                                  gaugeDetails.data.gauge_controller
                                    ?.gauge_future_relative_weight
                                )}
                              </span>
                            </div>
                            {gaugeDetails.data.apy_data
                              ?.gauge_future_crv_apy && (
                              <div className="detail-item">
                                <span className="label">APR:</span>
                                <span className="value">
                                  {gaugeDetails.data.apy_data.gauge_future_crv_apy.min_boost?.toFixed(
                                    2
                                  ) || '0.00'}
                                  % →{' '}
                                  {gaugeDetails.data.apy_data.gauge_future_crv_apy.max_boost?.toFixed(
                                    2
                                  ) || '0.00'}
                                  %
                                </span>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="votes-section">
                    <div style={{ textAlign: 'center' }}>
                      <button
                        onClick={toggleVoteData}
                        className="votes-toggle-button"
                      >
                        <i className="fas fa-vote-yea"></i>{' '}
                        {showVotes ? 'Hide Gauge Votes' : 'Show Gauge Votes'}
                      </button>
                    </div>

                    {showVotes && (
                      <div className="votes-content">
                        <h3>Gauge Votes</h3>

                        {voteLoading ? (
                          <div className="loading-centered">
                            Loading vote data...
                          </div>
                        ) : voteError ? (
                          <div className="error">{voteError}</div>
                        ) : voteData.length > 0 ? (
                          <>
                            <div className="table-container">
                              <table className="vote-table">
                                <thead>
                                  <tr>
                                    <th>Account</th>
                                    <th>Date</th>
                                    <th className="weight-column">Weight</th>
                                    <th className="amount-column">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {voteData.map((vote) => (
                                    <tr key={vote.id}>
                                      <td className="account-column">
                                        <a
                                          href={getEtherscanLink(vote.account)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="black-link"
                                        >
                                          {formatAddress(
                                            vote.account_alias || vote.account
                                          )}
                                        </a>
                                      </td>
                                      <td>{formatDate(vote.date_str)}</td>
                                      <td className="weight-column monospace">
                                        {formatWeight(vote.weight)}
                                      </td>
                                      <td className="amount-column monospace">
                                        {formatNumber(vote.amount)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {totalPages > 1 && (
                              <div className="pagination">
                                <button
                                  disabled={currentPage === 1}
                                  onClick={() => fetchVoteData(currentPage - 1)}
                                  className="page-button"
                                >
                                  Previous
                                </button>
                                <span>
                                  Page {currentPage} of {totalPages}
                                </span>
                                <button
                                  disabled={currentPage === totalPages}
                                  onClick={() => fetchVoteData(currentPage + 1)}
                                  className="page-button"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="no-votes">
                            No votes found for this gauge.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="modal-error">
                  <p>Error loading gauge details.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GaugeSearch;
